import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/Skeleton';
import {
  Activity,
  MessageSquare,
  GitBranch,
  AlertCircle,
  Clock,
  Bookmark,
  ChevronRight
} from 'lucide-react';
import { formatDate } from '@/lib/utils';

const FeedTab = ({ events, projects, onStarToggle, loading }) => {
  const navigate = useNavigate();

  // Get icon based on event type
  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'message':
        return MessageSquare;
      case 'pr_merged':
      case 'pr_opened':
        return GitBranch;
      case 'issue_update':
        return AlertCircle;
      default:
        return Activity;
    }
  };

  // Group events by project_id
  const groupedEvents = events.reduce((acc, event) => {
    const projectId = event.project_id;
    if (!acc[projectId]) {
      acc[projectId] = [];
    }
    acc[projectId].push(event);
    return acc;
  }, {});

  // Get project details by id
  const getProject = (projectId) => {
    return projects.find(p => p.id === projectId) || {
      id: projectId,
      name: 'Unknown Project',
      color: '#94a3b8'
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10">
              <Skeleton className="h-6 w-48" />
            </div>
            <div className="p-4 space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  // Empty state
  if (!events || events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 max-w-4xl mx-auto">
        <div className="rounded-full bg-black/[0.04] dark:bg-white/[0.08] p-6 mb-4">
          <Activity className="h-12 w-12 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold mb-2">No feed updates yet</h3>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          Your project activity and updates will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {Object.entries(groupedEvents).map(([projectId, projectEvents]) => {
        const project = getProject(projectId);

        return (
          <Card key={projectId} className="overflow-hidden">
            {/* Project Header */}
            <div className="px-6 py-4 border-b border-white/10 bg-white/20 dark:bg-white/5 backdrop-blur-md">
              <div className="flex items-center gap-3">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: project.color?.startsWith('#')
                      ? project.color
                      : undefined
                  }}
                  {...(!project.color?.startsWith('#') && project.color && {
                    className: cn("w-2 h-2 rounded-full", project.color)
                  })}
                />
                <h3 className="font-semibold text-lg">{project.name}</h3>
                <span className="text-xs text-muted-foreground ml-auto">
                  {projectEvents.length} {projectEvents.length === 1 ? 'update' : 'updates'}
                </span>
              </div>
            </div>

            {/* Event Items */}
            <div className="p-2">
              {projectEvents.map((event) => {
                const Icon = getEventIcon(event.event_type);

                return (
                  <div
                    key={event.id}
                    className="group rounded-xl mx-2 my-1 border border-black/[0.08] dark:border-white/[0.06] bg-white/40 dark:bg-white/[0.02]"
                  >
                    <div
                      className="p-4 cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/[0.03] rounded-xl"
                      onClick={() => {
                        if (event.external_url) {
                          window.open(event.external_url, '_blank');
                        }
                      }}
                    >
                      <div className="flex gap-3">
                        {/* Icon */}
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-xl bg-black/[0.04] dark:bg-white/[0.08] flex items-center justify-center">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-medium text-sm">{event.title}</h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                "h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity",
                                event.starred && "opacity-100"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                onStarToggle?.(event.id, !event.starred);
                              }}
                            >
                              <Bookmark
                                className={cn(
                                  "h-4 w-4",
                                  event.starred && "fill-current"
                                )}
                              />
                            </Button>
                          </div>

                          {/* Timestamp */}
                          <div className="flex items-center gap-1 mb-2">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {formatDate(event.timestamp)}
                            </span>
                          </div>

                          {/* Content Preview */}
                          {event.content && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                              {event.content}
                            </p>
                          )}

                          {/* Author */}
                          {event.author_avatar && (
                            <div className="flex items-center gap-2">
                              <Avatar className="h-6 w-6">
                                <AvatarImage src={event.author_avatar} />
                                <AvatarFallback>
                                  {event.author_name?.charAt(0) || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              {event.author_name && (
                                <span className="text-xs text-muted-foreground">
                                  {event.author_name}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}

      {/* View All Button */}
      <div className="flex justify-center pt-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/feed')}
          className="gap-2"
        >
          View all updates
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default FeedTab;
